import type {
  BundleItem,
  BundlePreview,
  BundleWorkflow,
  WorkflowView,
} from '@/lib/api/generated/model';

import { counted } from '../upload/bundleReview';

/**
 * What a bundle carries, said in one line, and the choice behind it (spec 211).
 *
 * Export and Share used to lead with a checklist of every agent and skill in
 * the workspace: eleven rows in the review's scene, six of them already ticked.
 * The rule behind the ticks (what the prompts name travels; the rest does not)
 * was never said. These functions say it, group the choice by that rule, and
 * draw the tree being sent. Pure, so the words and the order are tested
 * without a dialog.
 */

export interface Choice {
  agents: ReadonlySet<string>;
  skills: ReadonlySet<string>;
}

/**
 * What travels before anyone changes it: every agent and skill a prompt names
 * (spec 165 marks both). Export, Share and Copy all start here, so a named
 * skill is never left behind by one of them.
 */
export function namedChoice(preview: BundlePreview): {
  agents: Set<string>;
  skills: Set<string>;
} {
  return {
    agents: new Set(groupsOf(preview.agents).named.map((item) => item.key)),
    skills: new Set(groupsOf(preview.skills).named.map((item) => item.key)),
  };
}

/**
 * Items split by whether a prompt names them, each in the service's order.
 * Never by tick: a row stays in its group whatever the person ticks.
 */
export function groupsOf(items: readonly BundleItem[]): {
  named: BundleItem[];
  also: BundleItem[];
} {
  return {
    named: items.filter((item) => item.suggested),
    also: items.filter((item) => !item.suggested),
  };
}

/**
 * One kind's part of the Includes line, for the agents or skills that will be sent:
 * all the named ones and nothing else, "6 agents named in prompts"; only some of
 * the named ones, "4 of 6 agents named in prompts" (4 sent, 6 named); named ones
 * plus others, "8 agents (6 named in prompts)" (8 sent, 6 of them named).
 */
function kindPhrase(
  items: readonly BundleItem[],
  chosen: ReadonlySet<string>,
  one: string,
  many: string,
): string {
  const named = items.filter((item) => item.suggested);
  const sent = items.filter((item) => chosen.has(item.key));
  const sentNamed = sent.filter((item) => item.suggested).length;
  const extra = sent.length - sentNamed;
  if (sent.length === 0) return `no ${many}`;
  if (extra === 0) {
    return sentNamed === named.length
      ? `${counted(sentNamed, one, many)} named in prompts`
      : `${String(sentNamed)} of ${counted(named.length, one, many)} named in prompts`;
  }
  return sentNamed === 0
    ? counted(sent.length, one, many)
    : `${counted(sent.length, one, many)} (${String(sentNamed)} named in prompts)`;
}

/**
 * The Includes line: workflows, then agents, then skills, exactly as they will
 * be sent; then how many pieces are left to the Library's version (spec 212).
 */
export function includesLine(
  preview: BundlePreview,
  choice: Choice,
  usesLibrary = 0,
): string {
  return [
    counted(preview.workflows.length, 'workflow', 'workflows'),
    kindPhrase(preview.agents, choice.agents, 'agent', 'agents'),
    kindPhrase(preview.skills, choice.skills, 'skill', 'skills'),
    ...(usesLibrary > 0
      ? [
          `${String(usesLibrary)} ${usesLibrary === 1 ? 'uses' : 'use'} the Library's`,
        ]
      : []),
  ].join(' · ');
}

export interface TreeRow {
  workflow: BundleWorkflow;
  depth: number;
}

/**
 * The travelling workflows as a tree from the root (spec 211 US3).
 *
 * Neither preview says which workflow links to which, so the links are read
 * from the workspace's own definitions: a sub-workflow step names its target's
 * key. Depth first, so each child is listed right under its parent; each
 * workflow once, under the first parent that reaches it. A workflow the walk
 * cannot place goes last rather than missing.
 */
export function workflowTree(
  workflows: readonly BundleWorkflow[],
  views: readonly WorkflowView[],
): TreeRow[] {
  const travelling = new Map(workflows.map((w) => [w.key, w]));
  const viewByKey = new Map(views.map((v) => [v.key, v]));
  const root = workflows.find((w) => w.root) ?? workflows[0];
  if (!root) return [];

  const rows: TreeRow[] = [];
  const placed = new Set<string>([root.key]);
  const queue: TreeRow[] = [{ workflow: root, depth: 0 }];
  while (queue.length > 0) {
    const row = queue.shift();
    if (!row) break;
    rows.push(row);
    const found: TreeRow[] = [];
    for (const key of linksOf(viewByKey.get(row.workflow.key))) {
      const child = travelling.get(key);
      if (!child || placed.has(key)) continue;
      placed.add(key);
      found.push({ workflow: child, depth: row.depth + 1 });
    }
    queue.unshift(...found);
  }
  for (const workflow of workflows) {
    if (!placed.has(workflow.key)) rows.push({ workflow, depth: 0 });
  }
  return rows;
}

function linksOf(view: WorkflowView | undefined): string[] {
  if (!view) return [];
  return view.phases.flatMap((phase) =>
    (phase.steps ?? []).flatMap((step) =>
      step.type === 'linked_workflow' && step.workflow ? [step.workflow] : [],
    ),
  );
}
